import { useState } from 'react';
import { adminApi, uploadFileToSignedUrl } from '../lib/api.ts';
import { Btn } from '../components/Btn.tsx';
import { ErrorMessage } from '../components/ErrorMessage.tsx';
import { Field, Select, TextArea } from '../components/Field.tsx';
import { Modal } from '../components/Modal.tsx';
import { DOCUMENT_ACCEPT } from '../lib/fileTypes.ts';
import type { CompanyDocument, Question } from '../lib/types.ts';

export type NotCompliantPayload = {
  comment?: string;
  reference_document_id?: string;
  reference_upload?: { path: string; name: string };
};

// "Not compliant" (reject) dialog: a comment and/or an optional reference
// document (adopt an existing library doc, or upload a one-off example). Submit
// is enabled once there's a comment OR a reference.
export function NotCompliantModal({
  question,
  documents,
  pending,
  error,
  onClose,
  onSubmit,
  displayName,
}: {
  question: Question;
  documents: CompanyDocument[];
  pending: boolean;
  error: Error | null;
  onClose: () => void;
  onSubmit: (payload: NotCompliantPayload) => void;
  /**
   * Display-name override for the Compliance Documents tab. When set, the modal
   * title uses this instead of the question label; defaults to the question label.
   */
  displayName?: string;
}) {
  const [comment, setComment] = useState('');
  const [refMode, setRefMode] = useState<'none' | 'adopt' | 'upload'>('none');
  const [refDocId, setRefDocId] = useState('');
  const [refUpload, setRefUpload] = useState<{ path: string; name: string } | null>(null);
  const [refUploading, setRefUploading] = useState(false);
  const [refUploadError, setRefUploadError] = useState<string | null>(null);
  const adoptable = documents.filter((d) => d.current_version);

  async function uploadReference(file: File) {
    setRefUploading(true);
    setRefUploadError(null);
    try {
      const presigned = await adminApi.post<{ path: string; url: string }>(
        '/api/documents/upload-url',
        { file_name: file.name, mime_type: file.type },
      );
      await uploadFileToSignedUrl(presigned.url, file);
      setRefUpload({ path: presigned.path, name: file.name });
    } catch {
      setRefUploadError('Upload failed. Please try again.');
    } finally {
      setRefUploading(false);
    }
  }

  return (
    <Modal
      open
      title={`Not compliant — ${displayName ?? question.label}`}
      size="md"
      onClose={onClose}
      footer={
        <>
          <Btn size="sm" variant="ghost" onClick={onClose}>
            Cancel
          </Btn>
          <Btn
            size="sm"
            variant="danger"
            disabled={
              (!comment.trim() &&
                !(refMode === 'adopt' && refDocId) &&
                !(refMode === 'upload' && refUpload)) ||
              refUploading
            }
            loading={pending}
            onClick={() =>
              onSubmit({
                ...(comment.trim() ? { comment: comment.trim() } : {}),
                ...(refMode === 'adopt' && refDocId ? { reference_document_id: refDocId } : {}),
                ...(refMode === 'upload' && refUpload ? { reference_upload: refUpload } : {}),
              })
            }
          >
            Send request
          </Btn>
        </>
      }
    >
      <div className="space-y-3">
        <Field label="Comment for the vendor" required>
          <TextArea
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder="Explain what needs to change…"
          />
        </Field>
        <Field label="Reference document (optional)">
          <Select
            value={refMode}
            onChange={(e) => {
              const m = e.target.value as 'none' | 'adopt' | 'upload';
              setRefMode(m);
              setRefDocId('');
              setRefUpload(null);
              setRefUploadError(null);
            }}
          >
            <option value="none">None</option>
            <option value="adopt">Adopt an existing document</option>
            <option value="upload">Upload a new example</option>
          </Select>
        </Field>
        {refMode === 'adopt' && (
          <Select
            aria-label="Choose a reference document"
            value={refDocId}
            onChange={(e) => setRefDocId(e.target.value)}
          >
            <option value="">— Select a document —</option>
            {adoptable.map((d) => (
              <option key={d.id} value={d.id}>
                {d.name} ({d.category})
              </option>
            ))}
          </Select>
        )}
        {refMode === 'upload' && (
          <div className="text-sm">
            <input
              type="file"
              aria-label="Upload a reference document"
              accept={DOCUMENT_ACCEPT}
              disabled={refUploading}
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) void uploadReference(f);
              }}
            />
            {refUploading && <span className="ml-2 text-xs text-slate-500">Uploading…</span>}
            {refUpload && <span className="ml-2 text-xs text-emerald-600">✓ {refUpload.name}</span>}
            {refUploadError && <span className="ml-2 text-xs text-red-600">{refUploadError}</span>}
          </div>
        )}
        {error && <ErrorMessage error={error} />}
      </div>
    </Modal>
  );
}
